import axios from 'axios';
import { checkIdMasking } from '@/services/idMasking';
import { refreshFlowToken } from '@/services/flowToken';

/**
 * 작업허가서 첨부파일 업로드 공통 mixin.
 * - 일반 첨부: 마스킹 검사 없이 BE 업로드
 * - 면허증 (doc.key === 'license') 첨부: LLM 으로 개인정보 마스킹 검사 후
 *   fullyMasked === true 인 파일만 BE 업로드. 미통과 시 사유 alert + 화면 inline 표시.
 *
 * AI 정확도 보강:
 *   파일당 MCP_RETRY_COUNT 회 동시 비동기 호출 → 모두 통과해야만 업로드 허용.
 *   1 회라도 fullyMasked = false 면 즉시 거절.
 *
 * Phase 2 - BE 영구 저장:
 *   PASS / 일반 첨부 → /api/public/work-permit/{planId}/file 로 multipart POST
 *   응답 메타(id, storagePath, ...) 를 doc.files[i] 에 보관.
 *   업로드를 사용하려면 mixin 사용 컴포넌트가 다음을 제공해야 한다:
 *     · planId  (computed 또는 prop) — 현재 작업 허가서 번호
 *     · permitType (prop) — "insurance" | "excavationPermit" | ...
 *   planId 가 비어 있으면 in-memory(doc.files push)만 수행 (하위 호환).
 *
 * doc 에 추가되는 상태:
 *   - maskingChecking : Boolean — 검사 진행 중 (UI 스피너용)
 *   - rejections      : Array   — 거절된 파일별 사유 목록
 *   - maskingError    : String  — 검사 자체가 실패한 경우 메시지
 *   - uploadError     : String  — BE 업로드 실패 시 메시지
 */

const MCP_RETRY_COUNT = 3;

/**
 * MCP 응답의 exposedFields/maskedFields 가 string | object 어떤 형태로 와도
 * 사람이 읽을 수 있는 문자열 배열로 정규화.
 */
function normalizeFieldList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    return item.field || item.name || item.label || item.fieldName || JSON.stringify(item);
  }).filter(Boolean);
}

/**
 * 동일 파일에 대해 MCP 를 N 회 동시 호출.
 * 모두 성공 + 모두 fullyMasked === true 인 경우만 'PASS'.
 * 일부라도 fullyMasked === false 인 경우 'REJECT' + 첫 거절 결과 반환.
 * 일부라도 호출 자체가 실패한 경우 'ERROR' + 에러 정보 반환.
 */
async function checkWithRetry(file) {
  const settled = await Promise.allSettled(
    Array.from({ length: MCP_RETRY_COUNT }, () => checkIdMasking(file))
  );

  const results = [];
  const errors = [];
  for (const s of settled) {
    if (s.status === 'fulfilled') results.push(s.value);
    else errors.push(s.reason);
  }

  if (errors.length > 0) {
    return {
      verdict: 'ERROR',
      errorCount: errors.length,
      totalCount: MCP_RETRY_COUNT,
      firstError: errors[0]
    };
  }

  const rejections = results.filter((r) => !(r && r.fullyMasked === true));
  if (rejections.length > 0) {
    return {
      verdict: 'REJECT',
      result: rejections[0],
      rejectedCount: rejections.length,
      totalCount: MCP_RETRY_COUNT
    };
  }

  return { verdict: 'PASS' };
}

/**
 * BE 에 파일 업로드. planId 가 있으면 실제 multipart 호출, 없으면 in-memory only.
 * 응답으로 받은 메타({id, fileName, storagePath, ...}) 를 doc.files 에 push.
 * 실패 시 doc.uploadError 세팅 + alert.
 */
async function uploadFileToBackend(vm, doc, file, planId, permitType, isRetry) {
  if (!planId) {
    // planId 미발급 상태면 임시 보관
    doc.files.push(file);
    return;
  }
  const fd = new FormData();
  fd.append('file', file);
  fd.append('permitType', permitType || '');
  fd.append('docKey', doc.key || '');
  try {
    const res = await axios.post(
      `/api/public/work-permit/${encodeURIComponent(planId)}/file`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
    const ok = res && res.data && (res.data.statusCode === 200 || res.data.statusCode === undefined);
    if (!ok) {
      const msg = (res && res.data && res.data.message) || '업로드 실패';
      vm.$set(doc, 'uploadError', `[${file.name}] ${msg}`);
      alert(`[${file.name}] BE 업로드 실패: ${msg}`);
      return;
    }
    const meta = res.data.payload || {};
    // file 객체 대신 BE 메타 + 표시용 name 을 함께 보관
    doc.files.push({
      id: meta.id,
      name: meta.fileName || file.name,
      size: meta.fileSize || file.size,
      contentType: meta.contentType || file.type,
      storagePath: meta.storagePath || '',
      uploadedAt: meta.uploadedAt || Date.now(),
      maskingChecked: !!meta.maskingChecked,
      maskingPassed: meta.maskingPassed === 1
    });
  } catch (e) {
    const status = e && e.response && e.response.status;
    const data = e && e.response && e.response.data;
    // 400: 차단된 파일 유형 (압축 등)
    if (status === 400 && data && data.message) {
      vm.$set(doc, 'uploadError', `[${file.name}] ${data.message}`);
      alert(`[${file.name}] ${data.message}`);
      return;
    }
    if (status === 422 && data && data.message === 'masking rejected') {
      // 서버 측 재검증에서 거절 (FE 검증을 우회한 경우 도달)
      const detail = (data.payload && data.payload.maskingDetail) || {};
      const exposed = Array.isArray(detail.exposedFields) ? detail.exposedFields.join(', ') : '(미상)';
      vm.$set(doc, 'uploadError',
        `[${file.name}] 서버 측 마스킹 재검증 거절 — 노출된 항목: ${exposed}`);
      alert(`[${file.name}] 서버 측 개인정보 마스킹 재검증 거절.\n노출된 항목: ${exposed}\n` +
        `(평가: ${detail.overallAssessment || '-'})`);
      return;
    }
    // 401: 신청 토큰 만료/무효 → 자동 갱신 후 1회 재시도(자가복구). 실패 시 파일 보존 + 재인증 안내.
    if (status === 401 && !isRetry) {
      const refreshed = await refreshFlowToken();
      if (refreshed) {
        return uploadFileToBackend(vm, doc, file, planId, permitType, true);
      }
      doc.files.push(file); // 유실 방지: 임시 보관 (재인증 후 저장 시 재업로드 유도)
      vm.$set(doc, 'uploadError',
        `[${file.name}] 인증이 만료되었습니다. 다시 인증 후 재업로드해 주세요.`);
      alert('인증이 만료되어 파일을 업로드하지 못했습니다.\n' +
        '작성 화면을 나가지 말고, 이름·이메일 인증을 다시 진행한 뒤 파일을 재선택해 주세요.\n' +
        '(선택한 파일은 화면에 임시 보관되었습니다.)');
      return;
    }
    const msg = (data && data.message) || (e && e.message) || 'unknown error';
    vm.$set(doc, 'uploadError', `[${file.name}] ${msg}`);
    alert(`[${file.name}] BE 업로드 실패: ${msg}`);
  }
}

export default {
  methods: {
    async handleDocFile(event, doc) {
      const files = Array.from(event.target.files || []);
      const inputEl = event.target;
      if (!files.length) {
        if (inputEl) inputEl.value = '';
        return;
      }
      if (!doc.files) this.$set(doc, 'files', []);
      this.$set(doc, 'uploadError', '');

      // mixin 사용 컴포넌트가 planId / permitType 를 제공하면 BE 업로드 수행
      const planId = (typeof this.workPermitPlanId === 'function')
        ? this.workPermitPlanId() : (this.workPermitPlanId || this.planId || '');
      const permitType = this.workPermitFormKey || this.permitFormKey || '';

      // 면허증이 아니면 마스킹 검사 없이 바로 BE 업로드
      if (doc.key !== 'license') {
        for (const file of files) {
          await uploadFileToBackend(this, doc, file, planId, permitType);
        }
        if (inputEl) inputEl.value = '';
        return;
      }

      // 새 업로드 시도 — 이전 거절/오류 기록 초기화
      this.$set(doc, 'rejections', []);
      this.$set(doc, 'maskingError', '');

      // 면허증 — LLM 마스킹 검사 (N 회 동시 호출)
      this.$set(doc, 'maskingChecking', true);
      try {
        for (const file of files) {
          const verdict = await checkWithRetry(file);

          if (verdict.verdict === 'PASS') {
            await uploadFileToBackend(this, doc, file, planId, permitType);
            continue;
          }

          if (verdict.verdict === 'ERROR') {
            const err = verdict.firstError;
            const msg = `[${file.name}] 개인정보 마스킹 검사 중 오류가 발생했습니다. ` +
              `(${verdict.errorCount}/${verdict.totalCount} 회 실패) ${(err && err.message) || err}`;
            this.$set(doc, 'maskingError', msg);
            alert(msg);
            continue;
          }

          // REJECT
          const r = verdict.result;
          const rejection = {
            fileName: file.name,
            documentSubtype: (r && r.documentSubtype) || '',
            exposedFields: normalizeFieldList(r && r.exposedFields),
            overallAssessment: (r && r.overallAssessment) || '개인정보 마스킹 처리가 필요합니다.',
            confidence: (r && r.confidence) || null,
            rejectedCount: verdict.rejectedCount,
            totalCount: verdict.totalCount
          };
          doc.rejections.push(rejection);

          const exposedStr = rejection.exposedFields.length
            ? rejection.exposedFields.join(', ')
            : '(미상)';
          alert(
            `[${file.name}] 개인정보 마스킹 미흡으로 업로드가 거부되었습니다.\n` +
            `(AI 검사 ${verdict.totalCount}회 중 ${verdict.rejectedCount}회 거절)\n\n` +
            `노출된 항목 : ${exposedStr}\n` +
            `AI 평가    : ${rejection.overallAssessment}\n\n` +
            `해당 항목을 마스킹 처리 후 다시 업로드해 주세요.`
          );
        }
      } finally {
        this.$set(doc, 'maskingChecking', false);
        if (inputEl) inputEl.value = '';
      }
    }
  }
};